import { ReactNode } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Code,
  Divider,
  Flex,
  SimpleGrid,
  Text,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import { CourtDto } from '../../../models/court/dto/court.dto';
import { VenueAgentDto } from '../../../models/court-recording/dto/venue-agent.dto';
import { typography } from '../../../theme/tokens';
import VenueAgentCameraAssignmentsControl from './VenueAgentCameraAssignmentsControl';
import VenueAgentProvisionedCourtsControl from './VenueAgentProvisionedCourtsControl';
import {
  DISK_ALERT_THRESHOLD_GB,
  EMPTY_VALUE,
  NTP_ALERT_THRESHOLD_MS,
  courtScopeDiff,
  formatCount,
  describeUploadGate,
  formatCourtList,
  formatDiskFree,
  formatNtpOffset,
  formatUploadGate,
  formatUptime,
  isDiskLow,
  isNtpDrifting,
  isSegmentReportRefused,
  relativeTime,
  vt,
} from '../utils/venue-agent.utils';

interface Props {
  agent: VenueAgentDto;
  organizationId: string;
  courts: CourtDto[];
  isLoadingCourts?: boolean;
}

type Tone = 'neutral' | 'warning' | 'danger';

const TONE_COLOR: Record<Tone, string | undefined> = {
  neutral: undefined,
  warning: 'orange.500',
  danger: 'red.500',
};

function Metric({
  label,
  value,
  tone = 'neutral',
  hint,
}: {
  label: string;
  value: ReactNode;
  tone?: Tone;
  hint?: string;
}) {
  const body = (
    <Box>
      <Text {...typography.formLabel} color="gray.500" mb={1}>
        {label}
      </Text>
      <Text
        fontSize="sm"
        fontFamily="mono"
        sx={{ fontVariantNumeric: 'tabular-nums' }}
        fontWeight={tone === 'neutral' ? '400' : '600'}
        color={TONE_COLOR[tone]}
      >
        {value}
      </Text>
    </Box>
  );
  return hint ? <Tooltip label={hint}>{body}</Tooltip> : body;
}

/**
 * Expanded per-agent telemetry — every heartbeat field the API stores, plus
 * the admin court-scope control.
 *
 * Everything here is nullable: an agent that has never beaten (NEVER SEEN)
 * has none of it, and a partially-upgraded fleet can be missing individual
 * fields. Each value degrades to an em-dash rather than rendering `NaN`,
 * `undefined`, or throwing.
 */
export default function VenueAgentTelemetryPanel({
  agent,
  organizationId,
  courts,
  isLoadingCourts,
}: Props) {
  const panelBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');

  const nameById = new Map(courts.map((court) => [court.id, court.name]));
  const scope = courtScopeDiff(agent);

  const dlqDepth = agent.lastDlqDepth ?? null;
  const hasDlq = dlqDepth != null && dlqDepth > 0;
  const neverSeen = !agent.lastSeenAt;
  // The upload gate is the only number on this panel that changes how the
  // PRODUCT behaves rather than describing how the box is doing: it is how
  // long a finished recording is withheld from the box that has to clip it,
  // and it is the whole point of the segment-length reporting below. Until
  // now neither value was rendered anywhere, so "why did this venue's
  // recording take sixteen minutes to appear?" had no answer short of
  // reading the API's env settings.
  const segmentRefused = isSegmentReportRefused(agent);

  return (
    <Box
      bg={panelBg}
      borderTopWidth="1px"
      borderColor={borderColor}
      px={6}
      py={5}
      data-testid="venue-agent-telemetry-panel"
    >
      {neverSeen ? (
        <Text fontSize="sm" color="gray.500" mb={5}>
          {vt('never_seen_before')} <Code>recorder/config.json</Code>{' '}
          {vt('never_seen_after')}
        </Text>
      ) : (
        <>
          {hasDlq && (
            <Alert status="error" borderRadius="md" mb={4} fontSize="sm">
              <AlertIcon />
              <Box>
                <Text fontWeight="600">
                  {dlqDepth === 1
                    ? vt('dlq_title_one')
                    : vt('dlq_title_many', { count: dlqDepth })}
                </Text>
                <Text fontSize="xs">{vt('dlq_body')}</Text>
              </Box>
            </Alert>
          )}

          {agent.lastError && (
            <Alert status="error" borderRadius="md" mb={4} fontSize="sm">
              <AlertIcon />
              <Box>
                <Text fontWeight="600">{vt('last_error')}</Text>
                <Code fontSize="xs" whiteSpace="pre-wrap">
                  {agent.lastError}
                </Code>
              </Box>
            </Alert>
          )}

          <SimpleGrid columns={{ base: 2, md: 3, xl: 4 }} spacing={5} mb={5}>
            <Metric
              label={vt('metric_agent_version')}
              value={agent.lastAgentVersion || EMPTY_VALUE}
            />
            <Metric label={vt('metric_uptime')} value={formatUptime(agent.lastUptimeSec)} />
            <Metric label={vt('metric_last_seen')} value={relativeTime(agent.lastSeenAt)} />
            <Metric
              label={vt('metric_disk_free')}
              value={formatDiskFree(agent.lastDiskFreeGb)}
              tone={isDiskLow(agent.lastDiskFreeGb) ? 'warning' : 'neutral'}
              hint={
                isDiskLow(agent.lastDiskFreeGb)
                  ? vt('disk_low_hint', { threshold: DISK_ALERT_THRESHOLD_GB })
                  : undefined
              }
            />
            <Metric
              label={vt('metric_upload_queue')}
              value={formatCount(agent.lastUploadQueueDepth)}
            />
            <Metric
              label={vt('metric_dlq_depth')}
              value={formatCount(dlqDepth)}
              tone={hasDlq ? 'danger' : 'neutral'}
              hint={
                hasDlq
                  ? vt('dlq_hint')
                  : undefined
              }
            />
            <Metric
              label={vt('metric_active_recordings')}
              value={formatCount(agent.lastActiveRecordings)}
            />
            <Metric
              label={vt('metric_ntp_offset')}
              value={formatNtpOffset(agent.lastNtpOffsetMs)}
              tone={isNtpDrifting(agent.lastNtpOffsetMs) ? 'warning' : 'neutral'}
              hint={
                isNtpDrifting(agent.lastNtpOffsetMs)
                  ? vt('ntp_drift_hint', { threshold: NTP_ALERT_THRESHOLD_MS })
                  : undefined
              }
            />
            <Metric
              label={vt('metric_upload_gate')}
              value={
                segmentRefused ? (
                  <>
                    {formatUploadGate(agent.uploadGateSeconds)}
                    {/* Spelled out rather than left to the warning colour and
                        the tooltip. This panel is collapsed by default, so a
                        colour nobody expands to see and a hint nobody hovers
                        are the same as no signal — and this particular state
                        means the gate is too SHORT for the box, which is the
                        bug this epic exists to close, coming back. */}
                    <Text fontSize="xs" fontWeight="400" color="orange.500">
                      {vt('segment_refused')}
                    </Text>
                  </>
                ) : (
                  formatUploadGate(agent.uploadGateSeconds)
                )
              }
              tone={segmentRefused ? 'warning' : 'neutral'}
              // The prose lives in `describeUploadGate` rather than inline: it
              // is the one hint on this panel that has to reason about TWO
              // fields plus an alert, and the version written inline claimed
              // in both branches to know whether the gate had been widened for
              // this box — which the dashboard cannot know, because the
              // deployment floor is a server env setting. See that function.
              hint={describeUploadGate(agent)}
            />
          </SimpleGrid>

          <Divider mb={5} />
        </>
      )}

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        <Box>
          <Text {...typography.formLabel} color="gray.500" mb={2}>
            {vt('court_scope')}
          </Text>

          {scope.hasMismatch && (
            <Alert
              status="warning"
              borderRadius="md"
              mb={3}
              fontSize="sm"
              data-testid="court-scope-mismatch"
            >
              <AlertIcon />
              <Box>
                <Text fontWeight="600">{vt('scope_disagree')}</Text>
                {scope.recordingUnauthorized.length > 0 && (
                  <Text fontSize="xs">
                    {vt('recording_unauthorized_before')}{' '}
                    <strong>
                      {formatCourtList(scope.recordingUnauthorized, nameById)}
                    </strong>{' '}
                    {vt('recording_unauthorized_after')}
                  </Text>
                )}
                {scope.provisionedNotRecorded.length > 0 && (
                  <Text fontSize="xs">
                    {vt('provisioned_not_recorded_before')}{' '}
                    <strong>
                      {formatCourtList(scope.provisionedNotRecorded, nameById)}
                    </strong>{' '}
                    {vt('provisioned_not_recorded_after')}
                  </Text>
                )}
              </Box>
            </Alert>
          )}

          <Flex direction="column" gap={2}>
            <Box>
              <Text fontSize="xs" color="gray.500">
                {vt('self_reported')}
              </Text>
              <Flex alignItems="center" gap={2}>
                <Text fontSize="sm">
                  {scope.selfReported === null
                    ? vt('not_reported')
                    : formatCourtList(scope.selfReported, nameById)}
                </Text>
                <Badge colorScheme="gray" fontSize="2xs">
                  {vt('hint_only')}
                </Badge>
              </Flex>
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.500">
                {vt('admin_provisioned')}
              </Text>
              <Text fontSize="sm">
                {scope.provisioned === null
                  ? vt('all_courts')
                  : formatCourtList(scope.provisioned, nameById)}
              </Text>
            </Box>
          </Flex>
        </Box>

        <VenueAgentProvisionedCourtsControl
          agent={agent}
          organizationId={organizationId}
          courts={courts}
          isLoadingCourts={isLoadingCourts}
        />
      </SimpleGrid>

      <Divider my={5} />

      <VenueAgentCameraAssignmentsControl
        agent={agent}
        organizationId={organizationId}
        courts={courts}
        isLoadingCourts={isLoadingCourts}
      />
    </Box>
  );
}
